import { ElementPropertyRule } from '@fullstory/proto/fs/services/search-settings';
import { useCallback, useState } from 'react';
import { multiValueInfo } from '@fullstory/util/vars';
import { ElementPropertyRuleConfig } from '../types';
import { elementPropertyRuleConfig } from '../utils';

export const useElementPropertyRuleConfig = (
  initialElementPropertyRule: ElementPropertyRule,
  definitionElementImmutable: boolean = false,
  v2Enabled: boolean = false,
) => {
  const [elementPropertyRule, _setElementPropertyRule] = useState<ElementPropertyRuleConfig>(
    elementPropertyRuleConfig(initialElementPropertyRule, v2Enabled),
  );

  const setElementPropertyRule = useCallback(
    (rule: ElementPropertyRuleConfig) => {
      const newRule = { ...rule };
      if (definitionElementImmutable) {
        // preserve initial named element id always
        delete newRule.any;
        delete newRule.selectors;
        newRule.namedElementId = initialElementPropertyRule.namedElementId;
        // also if the definition element is selected as the child
        // switch to a childless rule
        if (newRule.childNamedElementId === initialElementPropertyRule.namedElementId) {
          delete newRule.childNamedElementId;
        }
      }
      if (newRule.usePropertyName && v2Enabled) {
        newRule.name = newRule.propertyName;
      }
      if (newRule.any) {
        // if parent any, need to ensure all child options are removed
        delete newRule.anyChild;
        delete newRule.childNamedElementId;
        delete newRule.childSelectors;
      }
      if (newRule.any || newRule.anyChild) {
        // in either case of parent or child any, we need to ensure attribute is the selected option
        if (!newRule.attribute) {
          delete newRule.innerText;
          delete newRule.static;
          newRule.attribute = {
            attribute: '',
            includeAncestors: false,
          };
        }
      }
      if (newRule.static) {
        // ensure we aren't using regex if the new rule is a static value
        delete newRule.regex;
      }
      const childOneOf = ElementPropertyRule.child_oneof(newRule);
      if (!childOneOf) {
        // need to convert property type to single if we don't have a child
        const { single } = multiValueInfo(newRule.propertyType);
        newRule.propertyType = single;
      }

      _setElementPropertyRule(newRule);
    },
    [initialElementPropertyRule, definitionElementImmutable, v2Enabled],
  );

  return {
    elementPropertyRule,
    setElementPropertyRule,
  };
};
